import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Chip,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateRight,
  OpenInNew,
  ImageOutlined,
  PictureAsPdfOutlined,
  Download,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@mui/icons-material';
import { UploadedImageType } from './ImageUploadModal.types';
import { extractFilename } from './DocumentChips';

/**
 * Theme colors - Light theme design
 */
const THEME_COLORS = {
  // Primary colors
  primary: '#232323',
  secondary: '#757575',
  // Background colors
  headerBg: '#FFFFFF',
  contentBg: '#F5F5F5',
  viewerBg: '#E8E8E8',
  // Accent colors
  vidaiOrange: '#E17E61',
  pdfRed: '#E53935',
  // UI colors
  border: '#E0E0E0',
  white: '#FFFFFF',
  // Arrow colors
  arrowBg: '#FFFFFF',
  arrowColor: '#232323',
  arrowHoverBg: '#F0F0F0',
  arrowShadow: '0 2px 8px rgba(0,0,0,0.15)',
};

// Icon button styles for header
const headerIconStyles = {
  color: THEME_COLORS.primary,
  backgroundColor: THEME_COLORS.contentBg,
  '&:hover': {
    backgroundColor: THEME_COLORS.border,
  },
  '&.Mui-disabled': {
    color: THEME_COLORS.secondary,
  },
};

export interface FileViewerModalProps {
  /** Whether modal is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Array of uploaded files to view */
  files: UploadedImageType[];
  /** Initial file index to show */
  initialIndex?: number;
  /** Modal title */
  title?: string;
}

/**
 * Helper to check if a file is a PDF based on name or URL
 */
const isPDFFile = (file: UploadedImageType): boolean => {
  return file.name?.toLowerCase().endsWith('.pdf') || 
         file.image?.startsWith('data:application/pdf') ||
         file.image?.toLowerCase().includes('.pdf');
};

/**
 * Check if image source is a URL (http/https) vs base64
 */
const isExternalUrl = (src: string): boolean => {
  return src.startsWith('http://') || src.startsWith('https://');
};

/**
 * Converts base64 PDF data to blob URL for proper iframe display
 * Returns null for external URLs (they can be used directly)
 */
const getPdfBlobUrl = (base64: string): string | null => {
  try {
    // If it's already a blob URL or external URL, return as-is
    if (base64.startsWith('blob:') || isExternalUrl(base64)) {
      return base64;
    }
    
    const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
    if (!base64Data) {
      return null;
    }
    
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error creating PDF blob:', error);
    return null;
  }
};

/**
 * FileViewerModal Component
 * 
 * A clean, light-themed viewer for previewing uploaded images and PDFs.
 * Features:
 * - Light theme design for better visibility
 * - Large, visible navigation arrows
 * - Image zoom and rotation
 * - PDF preview with fullscreen option
 * - Thumbnail strip for quick navigation
 */
const FileViewerModal: React.FC<FileViewerModalProps> = ({
  open,
  onClose,
  files,
  initialIndex = 0,
  title = 'Uploaded Files',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pdfBlobUrls, setPdfBlobUrls] = useState<Map<number, string>>(new Map());

  const currentFile = files[currentIndex];
  const isPDF = currentFile ? isPDFFile(currentFile) : false;
  const totalFiles = files.length;

  // Generate PDF blob URLs when files change (for base64 PDFs)
  // External URLs (S3) can be used directly
  React.useEffect(() => {
    const newBlobUrls = new Map<number, string>();
    
    files.forEach((file, index) => {
      if (isPDFFile(file) && file.image) {
        const blobUrl = getPdfBlobUrl(file.image);
        if (blobUrl) {
          newBlobUrls.set(index, blobUrl);
        }
      }
    });
    
    setPdfBlobUrls(newBlobUrls);
    
    // Cleanup: Only revoke blob URLs, not external URLs
    return () => {
      newBlobUrls.forEach((url) => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [files]);

  const currentPdfBlobUrl = isPDF ? pdfBlobUrls.get(currentIndex) : null;

  const handleFileChange = useCallback((newIndex: number) => {
    setCurrentIndex(newIndex);
    setZoom(1);
    setRotation(0);
  }, []);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      handleFileChange(currentIndex - 1);
    }
  }, [currentIndex, handleFileChange]);

  const goToNext = useCallback(() => {
    if (currentIndex < totalFiles - 1) {
      handleFileChange(currentIndex + 1);
    }
  }, [currentIndex, totalFiles, handleFileChange]);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  const handleOpenInNewTab = () => {
    if (isPDF && currentPdfBlobUrl) {
      window.open(currentPdfBlobUrl, '_blank');
    } else if (currentFile?.image) {
      window.open(currentFile.image, '_blank');
    }
  };

  const handleDownload = () => {
    const downloadUrl = isPDF && currentPdfBlobUrl ? currentPdfBlobUrl : currentFile?.image;
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = currentFile?.name || `file-${currentIndex + 1}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleFullscreen = () => setIsFullscreen(prev => !prev);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      switch (e.key) {
      case 'ArrowLeft':
        goToPrevious();
        break;
      case 'ArrowRight':
        goToNext();
        break;
      case 'Escape':
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
        break;
      case '+':
      case '=':
        if (!isPDF) handleZoomIn();
        break;
      case '-':
        if (!isPDF) handleZoomOut();
        break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, goToPrevious, goToNext, isFullscreen, onClose, isPDF]);

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setZoom(1);
      setRotation(0);
      setIsFullscreen(false);
    }
  }, [open, initialIndex]);

  if (!files.length) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isMobile}
      PaperProps={{
        sx: {
          width: isFullscreen ? '100vw' : isMobile ? '100%' : isTablet ? '90vw' : '80vw',
          height: isFullscreen ? '100vh' : isMobile ? '100%' : '85vh',
          maxWidth: isFullscreen ? 'none' : '1200px',
          borderRadius: isFullscreen || isMobile ? 0 : 3,
          overflow: 'hidden',
          backgroundColor: THEME_COLORS.contentBg,
        },
      }}
    >
      {/* Header - Light Theme */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: THEME_COLORS.headerBg,
          color: THEME_COLORS.primary,
          py: 1.5,
          px: 2,
          borderBottom: `1px solid ${THEME_COLORS.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              fontSize: '1rem',
              color: THEME_COLORS.primary,
            }}
          >
            {title}
          </Typography>
          <Chip
            label={`${currentIndex + 1} / ${totalFiles}`}
            size="small"
            sx={{
              backgroundColor: THEME_COLORS.contentBg,
              color: THEME_COLORS.primary,
              fontWeight: 600,
              border: `1px solid ${THEME_COLORS.border}`,
            }}
          />
          <Chip
            icon={isPDF 
              ? <PictureAsPdfOutlined sx={{ color: `${THEME_COLORS.pdfRed} !important` }} /> 
              : <ImageOutlined sx={{ color: `${THEME_COLORS.vidaiOrange} !important` }} />
            }
            label={isPDF ? 'PDF' : 'Image'}
            size="small"
            sx={{
              backgroundColor: isPDF ? 'rgba(229,57,53,0.1)' : 'rgba(225,126,97,0.1)',
              color: isPDF ? THEME_COLORS.pdfRed : THEME_COLORS.vidaiOrange,
              fontWeight: 500,
            }}
          />
        </Box>

        {/* Control Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!isPDF && (
            <>
              <IconButton onClick={handleZoomOut} disabled={zoom <= 0.5} sx={headerIconStyles} size="small">
                <ZoomOut fontSize="small" />
              </IconButton>
              <Typography sx={{ color: THEME_COLORS.primary, fontSize: '0.8rem', minWidth: 40, textAlign: 'center', fontWeight: 600 }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <IconButton onClick={handleZoomIn} disabled={zoom >= 3} sx={headerIconStyles} size="small">
                <ZoomIn fontSize="small" />
              </IconButton>
              <IconButton onClick={handleRotate} sx={headerIconStyles} size="small">
                <RotateRight fontSize="small" />
              </IconButton>
            </>
          )}
          {isPDF && (
            <IconButton onClick={toggleFullscreen} sx={headerIconStyles} size="small">
              {isFullscreen ? <FullscreenExitOutlined fontSize="small" /> : <FullscreenOutlined fontSize="small" />}
            </IconButton>
          )}
          <IconButton onClick={handleDownload} sx={headerIconStyles} size="small">
            <Download fontSize="small" />
          </IconButton>
          <IconButton onClick={handleOpenInNewTab} sx={headerIconStyles} size="small">
            <OpenInNew fontSize="small" />
          </IconButton>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              ...headerIconStyles, 
              ml: 1,
            }} 
            size="small"
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: THEME_COLORS.contentBg,
          overflow: 'hidden',
        }}
      >
        {/* Main Viewer Area with Navigation */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* Left Arrow - Outside content area */}
          {totalFiles > 1 && (
            <Box
              sx={{
                width: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconButton
                onClick={goToPrevious}
                disabled={currentIndex === 0}
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: THEME_COLORS.arrowBg,
                  color: THEME_COLORS.arrowColor,
                  boxShadow: THEME_COLORS.arrowShadow,
                  border: `1px solid ${THEME_COLORS.border}`,
                  '&:hover': {
                    backgroundColor: THEME_COLORS.arrowHoverBg,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: THEME_COLORS.contentBg,
                    color: THEME_COLORS.secondary,
                    boxShadow: 'none',
                  },
                }}
              >
                <ChevronLeft sx={{ fontSize: 28 }} />
              </IconButton>
            </Box>
          )}

          {/* Content Viewer Area */}
          <Box
            sx={{
              flex: 1,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: THEME_COLORS.viewerBg,
              borderRadius: 2,
              overflow: 'hidden',
              m: totalFiles > 1 ? 0 : 2,
            }}
          >
            {isPDF ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                {currentPdfBlobUrl ? (
                  <iframe
                    src={currentPdfBlobUrl}
                    title={currentFile?.name || 'PDF Preview'}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: 8,
                      backgroundColor: THEME_COLORS.white,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      color: THEME_COLORS.secondary,
                    }}
                  >
                    <PictureAsPdfOutlined sx={{ fontSize: 64, color: THEME_COLORS.pdfRed }} />
                    <Typography>Loading PDF...</Typography>
                  </Box>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  overflow: 'auto',
                  p: 3,
                }}
              >
                <img
                  src={currentFile?.image}
                  alt={currentFile?.name || 'Preview'}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    transform: `scale(${zoom}) rotate(${rotation}deg)`,
                    transition: 'transform 0.2s ease',
                    borderRadius: 8,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Right Arrow - Outside content area */}
          {totalFiles > 1 && (
            <Box
              sx={{
                width: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconButton
                onClick={goToNext}
                disabled={currentIndex === totalFiles - 1}
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: THEME_COLORS.arrowBg,
                  color: THEME_COLORS.arrowColor,
                  boxShadow: THEME_COLORS.arrowShadow,
                  border: `1px solid ${THEME_COLORS.border}`,
                  '&:hover': {
                    backgroundColor: THEME_COLORS.arrowHoverBg,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: THEME_COLORS.contentBg,
                    color: THEME_COLORS.secondary,
                    boxShadow: 'none',
                  },
                }}
              >
                <ChevronRight sx={{ fontSize: 28 }} />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* File Name */}
        <Box
          sx={{
            py: 1,
            px: 2,
            backgroundColor: THEME_COLORS.headerBg,
            borderTop: `1px solid ${THEME_COLORS.border}`,
          }}
        >
          <Typography
            sx={{
              color: THEME_COLORS.primary,
              fontSize: '0.875rem',
              fontWeight: 500,
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {extractFilename(currentFile?.name || currentFile?.image || `File ${currentIndex + 1}`)}
          </Typography>
        </Box>

        {/* Thumbnail Strip - Only show if multiple files */}
        {totalFiles > 1 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              py: 1.5,
              px: 2,
              backgroundColor: THEME_COLORS.headerBg,
              overflowX: 'auto',
              borderTop: `1px solid ${THEME_COLORS.border}`,
            }}
          >
            {files.map((file, index) => {
              const isFilePDF = isPDFFile(file);
              const isActive = index === currentIndex;
              
              return (
                <Paper
                  key={index}
                  onClick={() => handleFileChange(index)}
                  elevation={isActive ? 4 : 1}
                  sx={{
                    width: 64,
                    height: 48,
                    flexShrink: 0,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    border: isActive 
                      ? `2px solid ${THEME_COLORS.vidaiOrange}` 
                      : '2px solid transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      borderColor: isActive 
                        ? THEME_COLORS.vidaiOrange 
                        : THEME_COLORS.border,
                    },
                  }}
                >
                  {isFilePDF ? (
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(229,57,53,0.1)',
                      }}
                    >
                      <PictureAsPdfOutlined sx={{ color: THEME_COLORS.pdfRed, fontSize: 24 }} />
                    </Box>
                  ) : (
                    <img
                      src={file.image}
                      alt={file.name || `Thumbnail ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FileViewerModal;
